class LooomPreviewController {
  constructor(root) {
    this.root = root;
    this.links = Array.from(root.querySelectorAll("[data-preview-link]"));
    this.target = root.querySelector("[data-preview-target]");
    this.image = root.querySelector(".mega-shop__preview-image, .mega-menu-card__image");
    this.background = root.querySelector(".mega-shop__preview-background");
    this.productImage = root.querySelector(".mega-shop__preview-product-image");
    this.title = root.querySelector("[data-preview-title]");
    this.subtitle = root.querySelector("[data-preview-subtitle]");
    this.description = root.querySelector("[data-preview-description]");
    this.detailsLink = root.querySelector("[data-preview-details-link]");
    this.imageCache = new Map();
    this.changeId = 0;
    this.backgroundNext = this.createImageLayer(this.background);
    this.productImageNext = this.createImageLayer(this.productImage);
  }

  connect(signal) {
    this.root.addEventListener("pointerenter", () => this.preloadImages(), { once: true, signal });

    this.links.forEach((link) => {
      link.addEventListener("mouseenter", () => this.setActive(link), { signal });
      link.addEventListener("focus", () => this.setActive(link), { signal });
    });
  }

  createImageLayer(image) {
    if (!image || image.tagName !== "IMG") return null;

    image.classList.add("is-visible");
    const next = image.cloneNode(false);
    next.removeAttribute("src");
    next.removeAttribute("srcset");
    next.removeAttribute("sizes");
    next.removeAttribute("alt");
    next.classList.remove("is-visible");
    next.setAttribute("aria-hidden", "true");
    image.parentElement.append(next);
    return next;
  }

  preloadImages() {
    this.links.forEach((link) => {
      this.loadImage(link.dataset.backgroundImage);
      this.loadImage(link.dataset.productImage);
      this.loadImage(link.dataset.image);
    });
  }

  loadImage(src) {
    if (!src) return Promise.resolve(false);
    if (this.imageCache.has(src)) return this.imageCache.get(src);

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = "async";
      image.fetchPriority = "low";
      image.onload = async () => {
        try {
          await image.decode();
        } catch (_) {
          // A completed image can still be painted when decode() is unavailable.
        }
        resolve(true);
      };
      image.onerror = () => resolve(false);
      image.src = src;
    });

    this.imageCache.set(src, promise);
    return promise;
  }

  swapLayer(currentKey, nextKey, src, alt) {
    const current = this[currentKey];
    const next = this[nextKey];
    if (!current || !next || !src || current.currentSrc === src || current.src === src) return;

    next.removeAttribute("srcset");
    next.removeAttribute("sizes");
    next.src = src;
    next.alt = alt || "";
    next.classList.add("is-visible");
    current.classList.remove("is-visible");
    this[currentKey] = next;
    this[nextKey] = current;
  }

  async setActive(link) {
    const changeId = ++this.changeId;
    this.links.forEach((item) => item.classList.toggle("is-active", item === link));
    if (this.target) this.target.href = link.href;
    if (this.detailsLink) this.detailsLink.href = link.href;

    const [backgroundReady, productImageReady, imageReady] = await Promise.all([
      this.loadImage(link.dataset.backgroundImage),
      this.loadImage(link.dataset.productImage),
      this.loadImage(link.dataset.image),
    ]);
    if (changeId !== this.changeId) return;

    if (this.title) this.title.textContent = link.dataset.title || link.textContent.trim();
    if (this.subtitle) this.subtitle.textContent = link.dataset.subtitle || "";
    if (this.description) this.description.textContent = link.dataset.description || "";
    if (backgroundReady) {
      this.swapLayer(
        "background",
        "backgroundNext",
        link.dataset.backgroundImage,
        link.dataset.backgroundAlt || link.dataset.title,
      );
    }
    if (productImageReady) {
      this.swapLayer(
        "productImage",
        "productImageNext",
        link.dataset.productImage,
        link.dataset.productImageAlt || link.dataset.title,
      );
    }
    if (imageReady && this.image && link.dataset.image) {
      this.image.src = link.dataset.image;
      this.image.srcset = "";
      this.image.alt = link.dataset.imageAlt || link.dataset.title || "";
    }
  }
}

class LooomMegaMenuShop extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.categoryButtons = Array.from(this.querySelectorAll("[data-category-button]"));
    this.categoryPanels = Array.from(this.querySelectorAll("[data-category-panel]"));
    this.previewControllers = this.categoryPanels.map((panel) => {
      const preview = new LooomPreviewController(panel);
      preview.connect(signal);
      return { key: panel.dataset.categoryPanel, preview };
    });

    this.categoryButtons.forEach((button) => {
      const activate = () => this.activateCategory(button.dataset.categoryButton);
      button.addEventListener("mouseenter", activate, { signal });
      button.addEventListener("focus", activate, { signal });
      button.addEventListener("click", activate, { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  activateCategory(key) {
    this.categoryButtons.forEach((button) => {
      const active = button.dataset.categoryButton === key;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-expanded", active ? "true" : "false");
    });

    this.categoryPanels.forEach((panel) => {
      const active = panel.dataset.categoryPanel === key;
      panel.classList.toggle("is-active", active);
      panel.hidden = !active;
      panel.setAttribute("aria-hidden", active ? "false" : "true");
    });
  }
}

class LooomMegaMenuPreview extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const preview = new LooomPreviewController(this);
    preview.connect(this.controller.signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

class LooomMegaMenuCollections extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.links = Array.from(this.querySelectorAll("[data-collection-link]"));
    this.productPanels = Array.from(this.querySelectorAll("[data-collection-products]"));
    this.storyPanels = Array.from(this.querySelectorAll("[data-collection-story]"));

    this.links.forEach((link) => {
      const activate = () => this.setActive(link.dataset.collectionLink);
      link.addEventListener("mouseenter", activate, { signal });
      link.addEventListener("focus", activate, { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  setActive(key) {
    this.links.forEach((link) => {
      const active = link.dataset.collectionLink === key;
      link.classList.toggle("is-active", active);
      link.setAttribute("aria-current", active ? "true" : "false");
    });

    [...this.productPanels, ...this.storyPanels].forEach((panel) => {
      const panelKey = panel.dataset.collectionProducts ?? panel.dataset.collectionStory;
      const active = panelKey === key;
      panel.classList.toggle("is-active", active);
      panel.hidden = !active;
      panel.setAttribute("aria-hidden", active ? "false" : "true");
    });
  }
}

if (!customElements.get("mega-menu-shop")) customElements.define("mega-menu-shop", LooomMegaMenuShop);
if (!customElements.get("mega-menu-preview")) customElements.define("mega-menu-preview", LooomMegaMenuPreview);
if (!customElements.get("mega-menu-collections")) {
  customElements.define("mega-menu-collections", LooomMegaMenuCollections);
}
